import { Stack, Typography } from '@mui/material';
import { AqlBuilderOperator, AqlFieldExpression, AqlBuilderField } from '../helpers/common';
import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import ListFilterSelectChipTrigger from '@common/ui/ListFilters/ListFilterSelectChipTrigger';
import ListFilterPopover from '@common/components/ListFilterPopover';
import ListFilterSelectTitle from '@common/ui/ListFilters/ListFilterSelectTitle';
import ListFilterSelectFooter from '@common/ui/ListFilters/ListFilterSelectFooter';
import AqlBuilderFilter from './AqlBuilderFilter';

interface FilterItem {
  id: string;
  expression?: AqlFieldExpression;
}

interface TagFiltersWrapperProps {
  field: AqlBuilderField;
  operators: AqlBuilderOperator[];
  expressions?: AqlFieldExpression[];
  onExpressionsChange: (newValue?: AqlFieldExpression[]) => void;
  label: string;
  opened?: boolean;
  setOpened: (opened: boolean) => void;
}

const createDefaultItem = () => ({ id: crypto.randomUUID() });

export function AqlBuilderFilterOrWrapper({
  field,
  operators,
  expressions = [],
  onExpressionsChange,
  label,
  opened = true,
  setOpened,
}: TagFiltersWrapperProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const defaultItem = useMemo(() => createDefaultItem(), []);
  const [openedFilterId, setOpenedFilterId] = useState<string | undefined>(() =>
    !expressions.length ? defaultItem.id : undefined,
  );

  const [filterItems, setFilterItems] = useState<FilterItem[]>(() =>
    expressions.length
      ? expressions.map((expression) => ({
          id: crypto.randomUUID(),
          expression,
        }))
      : [defaultItem],
  );

  useEffect(() => {
    if (filterItems.length === 0) setFilterItems([createDefaultItem()]);
  }, [filterItems]);

  useEffect(() => {
    if (ref.current) setAnchorEl(ref.current);
  }, [opened, ref]);

  const triggerValues = useMemo(() => {
    const activeFilters = filterItems.filter((item) => item.expression);
    return [{ label: activeFilters.length ? `${activeFilters.length} Tag${activeFilters.length > 1 ? 's' : ''}` : '' }];
  }, [filterItems]);

  const handlePopoverOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setOpened(true);
    },
    [setOpened],
  );

  const handlePopoverClose = useCallback(() => {
    setAnchorEl(null);
    setOpenedFilterId(undefined);
    setOpened(false);
    setFilterItems((items) => items.filter((item) => item.expression));
  }, [setOpened]);

  const handleExpressionChange = useCallback(
    (id: string, expression?: AqlFieldExpression) => {
      setFilterItems((items) => {
        const newItems = items.map((item) => (item.id === id ? { ...item, expression } : item));
        const validExpressions = newItems.flatMap((item) => (item.expression ? [item.expression] : []));
        onExpressionsChange(validExpressions.length ? validExpressions : undefined);
        return newItems;
      });
    },
    [onExpressionsChange],
  );

  const handleAddFilter = useCallback(() => {
    setFilterItems((prev) => [...prev, createDefaultItem()]);
  }, []);

  const handleRemoveFilter = useCallback(
    (id: string) => {
      setFilterItems((prev) => prev.filter((item) => item.id !== id));
      handleExpressionChange(id, undefined);
    },
    [handleExpressionChange],
  );

  const handleClearAll = useCallback(() => {
    setFilterItems([]);
    onExpressionsChange(undefined);
  }, [onExpressionsChange]);

  return (
    <>
      <ListFilterSelectChipTrigger
        ref={ref}
        active={opened}
        label={label}
        value={triggerValues}
        onClear={triggerValues.length ? handleClearAll : undefined}
        onClick={handlePopoverOpen}
      />
      <ListFilterPopover open={!!anchorEl && opened} anchorEl={anchorEl} onClose={handlePopoverClose}>
        <ListFilterSelectTitle title={`Select ${label}`} />
        <Stack spacing={2} p={1}>
          {filterItems.map((item, index) => (
            <Stack key={item.id} spacing={1} alignItems="center" direction="row">
              {index > 0 && (
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              )}
              <AqlBuilderFilter
                field={field}
                label={label}
                operators={operators}
                expression={item.expression}
                opened={openedFilterId === item.id}
                onClear={() => handleRemoveFilter(item.id)}
                active={!!item.expression}
                setOpened={(open) => setOpenedFilterId(open ? item.id : undefined)}
                onExpressionChange={(expression) => handleExpressionChange(item.id, expression)}
              />
            </Stack>
          ))}
        </Stack>
        <ListFilterSelectFooter onAction={handleAddFilter} actionText={`New ${label}`} />
      </ListFilterPopover>
    </>
  );
}
